/**
 * <video-facade>
 *
 * Renders a poster image until the visitor presses play, then swaps in the
 * YouTube iframe. Keeps ~1MB of player scripts off the initial page load.
 *
 * Markup:
 *   <video-facade data-video-id="abc123" data-title="Video title">
 *     <button data-component="trigger" type="button">…poster…</button>
 *   </video-facade>
 *
 * Children are looked up by data-component, so classes stay free for styling.
 *
 * Attributes:
 *   data-video-id  YouTube video id. Required.
 *   data-title     iframe title. Falls back to the trigger's aria-label.
 *   data-params    Extra query params, e.g. "start=30&cc_load_policy=1".
 */
if (!customElements.get('video-facade')) {
	
const PLAYER_ALLOW = [
  'accelerometer',
  'autoplay',
  'clipboard-write',
  'encrypted-media',
  'gyroscope',
  'picture-in-picture',
  'web-share',
].join('; ');

class VideoFacade extends HTMLElement {
  connectedCallback() {
    // connectedCallback can fire while the parser is still inside this element,
    // before its children exist — that happens whenever the definition is
    // registered before the markup is parsed. Querying then returns null, so
    // wait for the document if we were upgraded mid-parse.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
    } else {
      this.init();
    }
  }

  init() {
    // Guard against the theme editor re-running this on an already-loaded node
    if (this.loaded) return;

    this.trigger = this.querySelector('[data-component="trigger"]');
    if (!this.trigger || !this.videoId) return;

    this.handleClick = this.load.bind(this);
    this.trigger.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    if (this.trigger && this.handleClick) {
      this.trigger.removeEventListener('click', this.handleClick);
    }
  }

  get videoId() {
    return this.dataset.videoId;
  }

  get loaded() {
    return this.dataset.loaded === 'true';
  }

  get embedSrc() {
    const params = new URLSearchParams(this.dataset.params || '');
    params.set('autoplay', '1');
    params.set('rel', '0');
    return `https://www.youtube.com/embed/${encodeURIComponent(this.videoId)}?${params}`;
  }

  load() {
    if (this.loaded) return;
    this.dataset.loaded = 'true';

    const frame = document.createElement('iframe');
    frame.className = 'video-text__iframe';
    frame.src = this.embedSrc;
    frame.title =
      this.dataset.title || this.trigger.getAttribute('aria-label') || 'YouTube video';
    frame.allow = PLAYER_ALLOW;
    frame.allowFullscreen = true;
    frame.setAttribute('frameborder', '0');

    this.trigger.replaceWith(frame);
    this.trigger = null;

    // The button that had focus is gone; move focus into the player so the
    // keyboard user is not dropped back at the top of the document.
    frame.focus();
  }
}

  customElements.define('video-facade', VideoFacade);
}
